import _ from 'lodash';
import { asyncRequestContextsActions } from '@lifeomic/phc-web-toolkit/src/redux/modules/asyncRequestContexts';
import { bindContextKeyToSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/asyncRequestContexts/selectors';
import { ButtonFilePicker } from '@lifeomic/chroma-react/components/ButtonFilePicker';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { evaluatedPolicySelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/evaluatedPolicy';
import { filesActions } from '../../../redux/modules/files';
import { filesPageActions } from '../../../redux/modules/pages/filesPage';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { notificationsActions } from '@lifeomic/phc-web-toolkit/src/redux/modules/notifications';
import { PageLayout } from '@lifeomic/chroma-react/components/PageLayout';
import { Paper } from '@lifeomic/chroma-react/components/Paper';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { PlusCircle } from '@lifeomic/chromicons';
import { projectPickerSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/projectPicker';
import { push } from 'connected-react-router';
import { SearchField } from '@lifeomic/chroma-react/components/SearchField';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@lifeomic/chroma-react/styles';
import {
  files as filesListSelector,
  isFileUploading as isFileUploadingSelector,
} from '../../../redux/selectors/filesSelector';
import contextKeys from '../../../redux/modules/asyncRequestContexts/contextKeys';
import EnforceABAC, {
  permissions,
} from '@lifeomic/phc-web-toolkit/src/components/EnforceABAC';
import FilePathCrumbs from './FilePathCrumbs';
import FilesTable from './FilesTable';
import Grid from '@mui/material/Grid';
import NotificationHelper from '@lifeomic/phc-web-toolkit/src/redux/util/NotificationHelper';
import PropTypes from 'prop-types';
import querystring from 'query-string';
import React from 'react';
import urls from '../../../util/urls';
import { Files as FilesIcon } from '@lifeomic/phc-web-toolkit/src/components/Icons';
import DetailsHeader from '@lifeomic/phc-web-toolkit/src/components/DetailsHeader';
import UserGuideLink from '@lifeomic/phc-web-toolkit/src/components/DetailsHeader/UserGuideLink';
import { LinearProgress } from '@lifeomic/chroma-react/components/LinearProgress';
import { ProjectReadMe } from '../../common/ProjectReadMe';
import { projectCohortPickerSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/projectCohortPicker';
import { enforce } from '@lifeomic/phc-web-toolkit/src/util/abac';

const intlLabels = {
  createFile: fileName => ({
    success: `Successfully uploaded file "${fileName}"`,
    failure: `Error uploading file "${fileName}"`,
  }),
};

const { createProjectFile, fetchProjectFiles } = filesActions;

const { changeFileInList, deleteFromFileList, addFileToList } =
  filesPageActions;

const styles = theme => ({
  root: {
    justifyContent: 'center',
  },
  content: {
    width: '100%',
    marginTop: theme.spacing(2),
    transition: '.25s ease-in',
    [theme.breakpoints.down('md')]: {
      transition: '.25s ease-in',
    },
  },
  filePathCrumbs: {
    marginBottom: theme.spacing(2),
  },
  searchButtonContainer: {
    marginTop: 10,
    '& button': {
      marginRight: 10,
    },
  },
  searchTerm: {
    color: theme.palette.primary.main,
  },
  uploadingFileMessage: {},
  paperStyles: {
    maxHeight: `calc(100vh - (${theme.pxToRem(217)} + ${theme.pxToRem(32)}))`,
    overflowY: 'scroll',
  },
  readMeContainer: {
    marginTop: theme.spacing(2),
  },
  help: {
    marginRight: theme.spacing(2),
  },
});

const messages = defineMessages({
  title: {
    id: 'pages.files.title',
    defaultMessage: 'Files',
  },
  uploading: {
    id: 'pages.files.uploading',
    defaultMessage: 'Uploading…',
  },
  fileSearchLabel: {
    id: 'pages.files.search.fileName.input',
    defaultMessage: 'Search files…',
  },
  invalidSearchErrorToast: {
    id: 'pages.files.search.invalidSearchErrorToast',
    defaultMessage:
      'An invalid search term has been provided. Please adjust your search term and try again.',
  },
  genericErrorToast: {
    id: 'pages.files.search.genericErrorToast',
    defaultMessage: 'An error occurred fetching files. Please try again.',
  },
});

const defaultFetchParams = { pageSize: 250 };

class Files extends React.PureComponent {
  componentDidMount() {
    const { activeProjectId, search, path } = this.props;

    this.fetchFiles({ projectId: activeProjectId, path, name: search });
  }

  async componentDidUpdate(prevProps) {
    const {
      activeProjectId,
      activeProjectCohortId,
      isFetching,
      search,
      path,
      push,
    } = this.props;
    const previousProjectId = _.get(prevProps, 'activeProject.id');
    const previousCohortId = _.get(prevProps, 'activeProjectCohortId');

    if (isFetching) return;

    // handle project changes
    if (activeProjectId && activeProjectId !== previousProjectId) {
      push(urls.app.files(''));
      this.fetchFiles({ projectId: activeProjectId, path: '' });
    }

    // handle cohort changes
    if (activeProjectCohortId !== previousCohortId) {
      push(urls.app.files(''));
      this.fetchFiles({ projectId: activeProjectId, path: '' });
    }

    // handle route changes
    if (path !== prevProps.path || search !== prevProps.search) {
      await this.fetchFiles({ projectId: activeProjectId, name: search, path });
    }
  }

  /**
   * Fetches files from the API and updates the activePath
   *
   * @param projectId - the active project ID
   * @param path - The "prefix" or path to search on. Can be one or many folder
   *               names separated by '/' (i.e., 'foo', 'foo/bar', 'foo/bar/baz')
   * @param name - The name of the file to search against. Only searches files
   *               in the current path
   * @param nextToken - The next token provided by the API to make a request
   *                    for more results
   */
  fetchFiles = async ({ projectId, path, name, nextToken }) => {
    const {
      addNotification,
      resetContext,
      fetchProjectFiles,
      intl: { formatMessage },
    } = this.props;

    const contextKey = contextKeys.filesPage(projectId, path).fetchFiles;

    resetContext(contextKey);
    await fetchProjectFiles(
      {
        id: projectId,
        params: {
          prefix: path,
          name,
          ...defaultFetchParams,
        },
        next: nextToken,
      },
      contextKey,
    );

    const hasError = this.props.hasErrorSelector;

    if (hasError) {
      if (hasError.error.includes("name can not contain '..' components")) {
        addNotification(formatMessage(messages.invalidSearchErrorToast), {
          statusType: 'error',
        });
      } else {
        addNotification(formatMessage(messages.genericErrorToast), {
          statusType: 'error',
        });
      }
    }
  };

  handlePathUpdate = ({ newPath, fileId, account, projectId }) => {
    const { push, activeProjectId, search } = this.props;

    push(urls.app.files({ fileId, account, projectId, path: newPath }));

    this.fetchFiles({
      projectId: activeProjectId,
      path: newPath,
      name: search,
    });
  };

  handleBreadcrumbClick = to => {
    push(urls.app.files({ path: to }));
  };

  handleFolderClick = ({ name, type }) => {
    const { path, push } = this.props;

    if (type !== 'FOLDER') {
      return;
    }

    const newPath = path === '' ? name : path.concat(`/${name}`);

    push(urls.app.files({ path: newPath }));
  };

  onDeleteFile = file => {
    this.props.deleteFromFileList(file?.id);
  };

  onFileAddToPath = (projectId, activePath) => e => {
    const file = e.target.files && e.target.files[0];

    if (!file) {
      return null;
    }

    const fileForCreate =
      activePath === '' ? file : new File([file], `${activePath}/${file.name}`);

    const { success, failure } = intlLabels.createFile(file.name);
    this.props.createProjectFile(
      projectId,
      fileForCreate,
      new NotificationHelper(success, failure),
    );
  };

  handleLoadMore = (projectId, path, nextToken) => async () => {
    await this.fetchFiles({ projectId, path, nextToken });
  };

  handleSearchInputSearch = search => {
    const { path, push } = this.props;

    push(urls.app.files({ search: search, path: path }));
  };

  handleFileSearchReset = () => {
    const { path, push } = this.props;

    push(urls.app.files({ path: path }));
  };

  handleRename = activePath => file => {
    if (file.name.startsWith(activePath)) {
      const subPathComponents = file.name
        .substr(activePath.length ? activePath.length + 1 : 0)
        .split('/');
      if (subPathComponents.length === 1) {
        // The file is still in this folder, but with a new name:
        const fileWithoutPath = {
          ...file,
          type: 'FILE',
          name: subPathComponents[0],
        };
        this.props.changeFileInList(fileWithoutPath);
      } else {
        // The file is moving into a sub-folder of this folder:
        this.props.deleteFromFileList(file.id);
        this.props.addFileToList({
          type: 'FOLDER',
          name: subPathComponents[0],
        });
      }
    } else {
      // the file is moving out into a totally different folder:
      this.props.deleteFromFileList(file.id);
    }
  };

  render() {
    const {
      path,
      activeProject,
      activeProjectId,
      classes,
      filesList,
      hasMoreToLoad,
      intl: { formatMessage },
      isFetching,
      isFileUploading,
      nextToken,
      policy,
      activeProjectCohortId,
    } = this.props;

    return (
      <React.Fragment>
        <PageLayout
          applyBackgroundCover
          maxWidth={false}
          left={
            <DetailsHeader
              title={formatMessage(messages.title)}
              icon={FilesIcon}
            />
          }
          headerCenter={
            <SearchField
              aria-label={formatMessage(messages.fileSearchLabel)}
              onSearch={this.handleSearchInputSearch}
              placeholder={formatMessage(messages.fileSearchLabel)}
              onClear={this.handleFileSearchReset}
              disableClearOnSearch
              fullWidth
            />
          }
          headerActions={
            <Box direction="row" align="center">
              <UserGuideLink name="files" className={classes.help} />
              <EnforceABAC permission={permissions.createData}>
                <ButtonFilePicker
                  variant="contained"
                  color="inverse"
                  icon={PlusCircle}
                  onChange={this.onFileAddToPath(activeProjectId, path)}
                  disabled={isFileUploading}
                >
                  <FormattedMessage
                    id="files.list.options.addFile"
                    defaultMessage="Add File"
                  />
                </ButtonFilePicker>
              </EnforceABAC>
            </Box>
          }
        >
          <Grid container spacing={0} className={classes.root}>
            <Grid item xs={12} className={classes.content}>
              <Grid container spacing={3}>
                {isFileUploading && (
                  <>
                    <Grid item xs={12}>
                      {formatMessage(messages.uploading)}
                    </Grid>
                    <Grid item xs={12}>
                      <LinearProgress variant="indeterminate" />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <FilePathCrumbs
                    containerClass={classes.filePathCrumbs}
                    baseUrl={urls.app.files}
                    path={path}
                    onPartClick={this.handleBreadcrumbClick}
                  />
                  <Paper className={classes.paperStyles} padding={0}>
                    <FilesTable
                      activePath={path}
                      enableDelete={enforce(permissions.deleteData, policy)}
                      enableEdit={enforce(permissions.updateData, policy)}
                      enableOpen={enforce(permissions.downloadFile, policy)}
                      enableRequestDownloadLink={enforce(
                        permissions.downloadFile,
                        policy,
                      )}
                      files={filesList}
                      handleDeleteFile={this.onDeleteFile}
                      handleLoadMore={this.handleLoadMore(
                        activeProjectId,
                        path,
                        nextToken,
                      )}
                      handleRenameFile={this.handleRename(path)}
                      hasActiveProject={!!activeProject}
                      hasMoreToLoad={hasMoreToLoad}
                      isFetching={isFetching}
                      onRowClick={this.handleFolderClick}
                      onUpdatePath={this.handlePathUpdate}
                    />
                  </Paper>
                  {/* Only show readme on root level */}
                  {!path && !activeProjectCohortId && (
                    <ProjectReadMe className={classes.readMeContainer} />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </PageLayout>
      </React.Fragment>
    );
  }
}

Files.propTypes = {
  changeFileInList: PropTypes.func.isRequired,
  classes: PropTypes.object,
  createProjectFile: PropTypes.func.isRequired,
  deleteFromFileList: PropTypes.func.isRequired,
  fetchProjectFiles: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  push: PropTypes.func.isRequired,
  resetContext: PropTypes.func.isRequired,
  addFileToList: PropTypes.func.isRequired,
};

const mapStateToProps = state => {
  const activeProject = projectPickerSelectors.selectActiveProject(state);
  const activeProjectId = _.get(activeProject, 'id', null);
  const search = querystring.parse(state.router.location.search).search || '';
  const path = querystring.parse(state.router.location.search).path || '';
  const contextKey = contextKeys.filesPage(activeProjectId, path).fetchFiles;
  const activeProjectCohortId =
    projectCohortPickerSelectors.selectActiveProjectCohortId(state);

  return {
    activeProject,
    activeProjectId,
    filesList: filesListSelector(state),
    hasErrorSelector:
      bindContextKeyToSelectors(contextKey).errorResponseSelector(state),
    hasMoreToLoad:
      bindContextKeyToSelectors(contextKey).hasNextTokenSelector(state),
    isFetching: bindContextKeyToSelectors(contextKey).isFetchingSelector(state),
    isFileUploading: isFileUploadingSelector(state),
    nextToken: bindContextKeyToSelectors(contextKey).nextTokenSelector(state),
    policy: evaluatedPolicySelectors.selectPolicyForUser(state),
    search,
    path,
    activeProjectCohortId,
  };
};

const mapDispatchToProps = {
  addNotification: notificationsActions.addNotification,
  changeFileInList,
  createProjectFile,
  deleteFromFileList,
  fetchProjectFiles,
  push,
  resetContext: asyncRequestContextsActions.resetContext,
  addFileToList,
};

export default compose(
  injectIntl,
  withStyles(styles),
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
)(Files);
